import { useEffect, useState } from "react";
import { PageHeader } from "../App";
import { 
  ChatCircleDots, 
  User, 
  Robot, 
  MagnifyingGlass,
  CaretRight,
  Funnel
} from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import api from "../lib/api";

const ConversationListItem = ({ conversation, isSelected, onClick }) => {
  const platformColors = {
    chatgpt: "bg-emerald-500",
    perplexity: "bg-blue-500",
    gemini: "bg-purple-500",
    copilot: "bg-amber-500",
    markdown: "bg-cyan-500",
    text: "bg-zinc-500",
    json: "bg-green-500",
  };
  
  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-4 border-b border-zinc-800/50 transition-colors duration-150 ${
        isSelected ? 'bg-zinc-800' : 'hover:bg-zinc-800/50'
      }`}
      data-testid={`conversation-item-${conversation.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-2 h-2 rounded-full mt-2 ${platformColors[conversation.source_platform] || 'bg-zinc-500'}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-zinc-200 truncate">
            {conversation.title || 'Untitled Conversation'}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-zinc-500">
              {conversation.message_count} messages
            </span>
            <span className="text-xs text-zinc-600">•</span>
            <span className="text-xs text-zinc-500 capitalize">
              {conversation.source_platform}
            </span>
          </div>
        </div>
        <CaretRight weight="bold" className="w-4 h-4 text-zinc-600 mt-1" />
      </div>
    </button>
  );
};

const Message = ({ message }) => {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  
  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
        isUser ? 'bg-blue-600' : 'bg-zinc-700'
      }`}>
        {isUser ? (
          <User weight="bold" className="w-4 h-4 text-white" />
        ) : (
          <Robot weight="bold" className="w-4 h-4 text-zinc-300" />
        )}
      </div>
      <div className={`message-bubble ${isUser ? 'user' : 'assistant'}`}>
        <p className="text-xs text-zinc-500 mb-2 uppercase tracking-wider">
          {message.role}
        </p>
        <div className="message-content text-sm text-zinc-200 whitespace-pre-wrap">
          {message.content}
        </div>
      </div>
    </div>
  );
};

export default function Conversations() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadConversations();
  }, [platformFilter]);

  const loadConversations = async () => {
    setLoading(true);
    try {
      const platform = platformFilter === "all" ? undefined : platformFilter;
      const data = await api.getConversations(0, 100, platform);
      setConversations(data.conversations || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error("Failed to load conversations:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadConversationDetail = async (id) => {
    try {
      const data = await api.getConversation(id);
      setSelectedConversation(data);
    } catch (error) {
      console.error("Failed to load conversation:", error);
    }
  };

  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    const title = conv.title || '';
    return title.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <div>
      <PageHeader 
        title="Conversations" 
        subtitle={`Browse ${total.toLocaleString()} parsed conversations`}
      />
      
      <div className="conversation-browser">
        {/* Left Panel - List */}
        <div className="conversation-list bg-zinc-950">
          {/* Filters */}
          <div className="p-4 border-b border-zinc-800 space-y-3">
            <div className="relative">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-zinc-900 border-zinc-800"
                data-testid="search-conversations"
              />
            </div>
            <Select value={platformFilter} onValueChange={setPlatformFilter}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="platform-filter">
                <Funnel weight="regular" className="w-4 h-4 mr-2 text-zinc-500" />
                <SelectValue placeholder="All Platforms" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Platforms</SelectItem>
                <SelectItem value="chatgpt">ChatGPT</SelectItem>
                <SelectItem value="perplexity">Perplexity</SelectItem>
                <SelectItem value="gemini">Gemini</SelectItem>
                <SelectItem value="copilot">Copilot</SelectItem>
                <SelectItem value="markdown">Markdown</SelectItem>
                <SelectItem value="text">Text</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Conversation List */}
          <ScrollArea className="h-[calc(100vh-200px)]">
            {loading ? (
              <div className="p-4 space-y-3">
                {[1,2,3,4,5].map(i => (
                  <div key={i} className="animate-pulse">
                    <div className="h-4 bg-zinc-800 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-zinc-800/50 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : filteredConversations.length > 0 ? (
              filteredConversations.map(conv => (
                <ConversationListItem
                  key={conv.id}
                  conversation={conv}
                  isSelected={selectedConversation?.id === conv.id}
                  onClick={() => loadConversationDetail(conv.id)}
                />
              ))
            ) : (
              <div className="p-8 text-center">
                <ChatCircleDots weight="thin" className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                <p className="text-zinc-500 text-sm">No conversations found</p>
              </div>
            )}
          </ScrollArea>
        </div>
        
        {/* Right Panel - Detail */}
        <ScrollArea className="conversation-detail bg-zinc-900/30">
          {selectedConversation ? (
            <div className="space-y-6">
              <div className="border-b border-zinc-800 pb-4">
                <h3 className="text-lg font-medium text-zinc-100">
                  {selectedConversation.title || 'Untitled Conversation'}
                </h3>
                <div className="flex items-center gap-3 mt-2">
                  <span className={`badge badge-${
                    selectedConversation.source_platform === 'chatgpt' ? 'green' :
                    selectedConversation.source_platform === 'perplexity' ? 'blue' :
                    selectedConversation.source_platform === 'gemini' ? 'purple' : 'yellow'
                  }`}>
                    {selectedConversation.source_platform}
                  </span>
                  <span className="text-xs text-zinc-500">
                    {selectedConversation.message_count} messages
                  </span>
                  <span className="text-xs font-mono text-zinc-600">
                    #{selectedConversation.content_hash}
                  </span>
                </div>
              </div>
              
              <div className="space-y-4">
                {selectedConversation.messages?.map((msg, idx) => (
                  <Message key={msg.id || idx} message={msg} />
                ))}
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <ChatCircleDots weight="thin" className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-500">Select a conversation to view details</p>
              </div>
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
}
